type PathLabel = {
  readonly labelKey: string
  readonly labelFallback: string
  readonly detail: string
}

function segmentAfter(segments: ReadonlyArray<string>, key: string): string | undefined {
  const idx = segments.indexOf(key)
  if (idx === -1 || idx >= segments.length - 1) return undefined
  return segments[idx + 1]
}

const PATTERNS: ReadonlyArray<{
  readonly test: (segments: ReadonlyArray<string>) => boolean
  readonly label: (segments: ReadonlyArray<string>) => PathLabel
}> = [
  {
    test: segments => segments.at(-1) === 'dashboard',
    label: () => ({ labelKey: 'navigation.project.summary', labelFallback: 'Summary', detail: '' }),
  },
  {
    test: segments => segments.at(-1) === 'notifications',
    label: () => ({ labelKey: 'navigation.project.mail', labelFallback: 'Notifications', detail: '' }),
  },
  {
    test: segments => segments.at(-1) === 'archive',
    label: () => ({ labelKey: 'navigation.settings.global.issuesList', labelFallback: 'Issues List', detail: '' }),
  },
  {
    test: segments => segments.at(-1) === 'sync-queue',
    label: () => ({ labelKey: 'syncQueue.syncQueue', labelFallback: 'Sync Queue', detail: '' }),
  },
  {
    test: segments => {
      const idx = segments.indexOf('reports')
      return idx !== -1 && idx === segments.length - 1
    },
    label: () => ({ labelKey: 'navigation.settings.global.reports', labelFallback: 'Reports', detail: '' }),
  },
  {
    test: segments => {
      const idx = segments.indexOf('reports')
      return idx !== -1 && idx < segments.length - 1
    },
    label: segments => {
      const id = segmentAfter(segments, 'reports') ?? ''
      return { labelKey: 'navigation.settings.global.reports', labelFallback: 'Report', detail: id ? `#${id}` : '' }
    },
  },
  {
    test: segments => {
      const idx = segments.indexOf('issues')
      return idx !== -1 && idx < segments.length - 1
    },
    label: segments => {
      const id = segmentAfter(segments, 'issues') ?? ''
      return { labelKey: 'project.issues.issue', labelFallback: 'Issue', detail: id ? `#${id}` : '' }
    },
  },
  {
    test: segments => {
      const idx = segments.indexOf('issues')
      return idx !== -1 && idx === segments.length - 1
    },
    label: () => ({ labelKey: 'navigation.settings.global.issues', labelFallback: 'Issues', detail: '' }),
  },
  {
    test: segments => segments.includes('settings'),
    label: () => ({ labelKey: 'navigation.project.settings', labelFallback: 'Settings', detail: '' }),
  },
]

export function pathToLabel(pathname: string): PathLabel {
  const segments = pathname.split('/').filter(Boolean)

  for (const pattern of PATTERNS) {
    if (pattern.test(segments)) {
      return pattern.label(segments)
    }
  }

  const last = segments.at(-1) ?? ''
  const fallback = last.charAt(0).toUpperCase() + last.slice(1)
  return { labelKey: '', labelFallback: fallback, detail: '' }
}
